import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FileButton, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconFileImport } from '@tabler/icons-react';
import { EssayContent, EssayNode, parseEssayDocx } from '../../hooks/useEssayApi';
import { showToast } from '../../utils/toast';

const hasText = (nodes: EssayNode[]): boolean =>
  nodes.some((n) => n.text.trim().length > 0 || hasText(n.children || []));

/**
 * «Импорт из Word»: сервер раскладывает файл по разделам, результат встаёт в
 * редактор, а сохраняет его обычный автосейв. Непустое эссе заменяем только
 * после подтверждения — иначе один клик стирал бы написанное.
 */
export const EssayDocxImportButton: React.FC<{
  content: EssayContent;
  onImport: (content: EssayContent) => void;
}> = ({ content, onImport }) => {
  const { t } = useTranslation();
  const [isParsing, setIsParsing] = useState(false);
  // Без сброса повторный выбор того же файла (поправил и загрузил снова) не
  // вызывает onChange.
  const resetRef = useRef<() => void>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setIsParsing(true);
    try {
      const parsed = await parseEssayDocx(file);
      const isFilled = Object.values(content).some(hasText);
      if (!isFilled) return onImport(parsed);
      modals.openConfirmModal({
        title: t('essays.importDocx'),
        centered: true,
        children: <Text size="sm">{t('essays.importDocxConfirm')}</Text>,
        labels: { confirm: t('essays.importDocxReplace'), cancel: t('common.cancel') },
        confirmProps: { color: 'red' },
        onConfirm: () => onImport(parsed),
      });
    } catch {
      // Текст сервера не показываем: он всегда русский и не переводится.
      showToast({
        title: t('essays.importDocxFailed'),
        message: t('essays.importDocxFailedMessage'),
        color: 'red',
      });
    } finally {
      setIsParsing(false);
      resetRef.current?.();
    }
  };

  return (
    <FileButton onChange={(file) => void handleFile(file)} accept=".docx" resetRef={resetRef}>
      {(props) => (
        <Button
          {...props}
          variant="default"
          leftSection={<IconFileImport size={16} />}
          loading={isParsing}
        >
          {t('essays.importDocx')}
        </Button>
      )}
    </FileButton>
  );
};
